import React, { useMemo } from 'react';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

export const IMPORT_CATEGORY_DEFAULTS = [
  'Electronics',
  'Processors',
  'Memory',
  'Storage',
  'Graphics',
  'Power',
  'Audio',
  'Video',
  'Mobile',
  'Networking',
  'Peripherals',
  'Software',
  'Accessories',
];

const filter = createFilterOptions<string>();

interface CreateCategoryOption {
  kind: 'create';
  value: string;
}

type CategoryOption = string | CreateCategoryOption;

const mergeCategoryOptions = (options: string[]): string[] => {
  const seen = new Set<string>();
  const merged: string[] = [];
  options.forEach((option) => {
    const normalized = option.trim();
    const key = normalized.toLowerCase();
    if (normalized && !seen.has(key)) {
      seen.add(key);
      merged.push(normalized);
    }
  });
  return merged;
};

export const ImportCategorySelect: React.FC<{
  value: string;
  options: string[];
  onChange: (value: string) => void;
  loading?: boolean;
  compact?: boolean;
  placeholder?: string;
}> = ({
  value,
  options,
  onChange,
  loading = false,
  compact = false,
  placeholder = 'Select or create category',
}) => {
  const mergedOptions = useMemo(() => mergeCategoryOptions(options), [options]);

  return (
    <Autocomplete<CategoryOption, false, false, true>
      className={
        compact
          ? 'ih-category-select ih-category-select-compact'
          : 'ih-category-select'
      }
      freeSolo
      fullWidth
      selectOnFocus
      handleHomeEndKeys
      disabled={loading}
      value={value || null}
      options={mergedOptions}
      aria-label='Category'
      getOptionLabel={(option) =>
        typeof option === 'string' ? option : option.value
      }
      filterOptions={(available, params) => {
        const stringOptions = available.filter(
          (option): option is string => typeof option === 'string',
        );
        const filtered: CategoryOption[] = filter(stringOptions, params);
        const input = params.inputValue.trim();
        const exists = stringOptions.some(
          (option) => option.toLowerCase() === input.toLowerCase(),
        );
        if (input && !exists) {
          filtered.push({ kind: 'create', value: input });
        }
        return filtered;
      }}
      renderOption={(props, option) => (
        <li
          {...props}
          key={typeof option === 'string' ? option : `create-${option.value}`}
        >
          {typeof option !== 'string' ? (
            <span style={{ color: 'var(--action-color)', fontWeight: 500 }}>
              + Create &ldquo;{option.value}&rdquo;
            </span>
          ) : (
            option
          )}
        </li>
      )}
      onChange={(_event, nextValue) => {
        onChange(
          typeof nextValue === 'string'
            ? nextValue
            : nextValue?.value ?? '',
        );
      }}
      onInputChange={(_event, nextInput, reason) => {
        if (reason === 'input') {
          onChange(nextInput);
        }
      }}
      componentsProps={{
        paper: {
          sx: {
            background: 'var(--bg-colors)',
            color: 'var(--text-primary-color)',
            border: '1px solid var(--es-soft-border)',
            fontFamily: 'Inter, sans-serif',
            fontSize: compact ? '12px' : '13px',
          },
        },
        popper: {
          sx: {
            zIndex: 1500,
            '& .MuiAutocomplete-option': {
              color: 'var(--text-primary-color)',
              fontSize: compact ? '12px' : '13px',
            },
            '& .MuiAutocomplete-option.Mui-focused': {
              background: 'var(--bg-hover) !important',
            },
            '& .MuiAutocomplete-option[aria-selected="true"]': {
              background: 'var(--bg-surface) !important',
            },
          },
        },
      }}
      sx={{
        '& .MuiOutlinedInput-root': {
          minHeight: compact ? '32px' : '38px',
          padding: compact ? '0 6px' : '2px 8px',
          borderRadius: compact ? '6px' : '7px',
          background: compact ? 'var(--background)' : 'var(--bg-colors)',
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter, sans-serif',
          fontSize: compact ? '12px' : '13px',
        },
        '& .MuiAutocomplete-input': {
          color: 'var(--text-primary-color)',
          fontFamily: 'Inter, sans-serif',
          fontSize: compact ? '12px' : '13px',
        },
        '& .MuiSvgIcon-root': { color: 'var(--text-secondary)' },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={loading ? 'Loading…' : placeholder}
        />
      )}
    />
  );
};
